import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, Switch, Button, Space, Avatar, Divider } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import * as services from "../service/emailLoopApi";
import styles from "../styles/EmailLoopModal.module.css";

const { TextArea } = Input;
const ALL_VALUE = "__ALL__";
const dividerStyle = { margin: "16px 0" };

const EmailLoopModal = ({
    open,
    record,
    onCancel,
    onSubmit,
    previewMemberCount,
    onOpenMemberPreview,
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [memberCount, setMemberCount] = useState(0);
    const [previewMembers, setPreviewMembers] = useState([]);

    const departmentIdList = Form.useWatch("departmentIdList", form);
    const positionIdList = Form.useWatch("positionIdList", form);

    const mergeByValue = (seed, fetched) => {
        const map = new Map(seed.map((o) => [o.value, o]));
        fetched.forEach((o) => map.set(o.value, o));
        return Array.from(map.values());
    };

    useEffect(() => {
        if (!open) return;

        const cond = record?.memberConditionList?.[0] || {};
        const seedDept = (cond.departmentList || []).map((d) => ({ label: d.name, value: d.id }));
        const seedPos = (cond.positionList || []).map((p) => ({ label: p.name, value: p.id }));

        setDepartmentOptions(seedDept);
        setPositionOptions(seedPos);

        services.getDepartmentOptions().then((res) => {
            const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
            const fetched = list.map((d) => ({ label: d.name, value: d.id }));
            setDepartmentOptions((prev) => mergeByValue(prev, fetched));
        });
        services.getPositionOptions().then((res) => {
            const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
            const fetched = list.map((p) => ({ label: p.positionName, value: p.id }));
            setPositionOptions((prev) => mergeByValue(prev, fetched));
        });
    }, [open, record]);

    const collapseToAllIfFull = (selectedIds, options) => {
        if (options.length > 0 && selectedIds.length === options.length) {
            return [ALL_VALUE];
        }
        return selectedIds;
    };

    const initialValues = useMemo(() => {
        if (!record) {
            return { isActive: true, departmentIdList: [], positionIdList: [] };
        }
        const cond = record.memberConditionList?.[0] || {};
        return {
            groupName: record.groupName,
            description: record.description,
            isActive: record.isActive === "Y",
            departmentIdList: cond.departmentList?.map((d) => d.id) || [],
            positionIdList: cond.positionList?.map((p) => p.id) || [],
        };
    }, [record]);


    const resolveRealIds = (fieldValues, options) => {
        if (fieldValues?.includes(ALL_VALUE)) {
            return options.map((o) => o.value);
        }
        return fieldValues || [];
    };

    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(async () => {
            const realDept = resolveRealIds(departmentIdList, departmentOptions);
            const realPos = resolveRealIds(positionIdList, positionOptions);
            const members = await previewMemberCount(realDept, realPos);
            setPreviewMembers(members);
            setMemberCount(members.length);
        }, 400);
        return () => clearTimeout(timer);
    }, [departmentIdList, positionIdList, departmentOptions, positionOptions, open, previewMemberCount]);

    // "ทั้งหมด" -> เก็บ tag เดียวคือ ALL_VALUE
    // เลือกตัวอื่นทับ -> เอา ALL_VALUE ออก
    const handleSelectChange = (fieldName) => (values) => {
        const hadAll = values.length > 1 && values.includes(ALL_VALUE);
        if (values[values.length - 1] === ALL_VALUE) {
            form.setFieldValue(fieldName, [ALL_VALUE]);
        } else if (hadAll) {
            form.setFieldValue(fieldName, values.filter((v) => v !== ALL_VALUE));
        } else {
            form.setFieldValue(fieldName, values);
        }
    };

    const withAllOption = (options) => [
        { label: t("email_loop.form.select_all"), value: ALL_VALUE },
        ...options,
    ];

    const handleSubmit = (values) => {
        onSubmit({
            ...values,
            departmentIdList: resolveRealIds(values.departmentIdList, departmentOptions),
            positionIdList: resolveRealIds(values.positionIdList, positionOptions),
        });
    };

    return (
        <Modal
            title={t("email_loop.form.title")}
            open={open}
            onCancel={onCancel}
            footer={null}
            width={520}
            centered
            destroyOnClose
            styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        >
            <Form
                form={form}
                key={record?.id ?? "new"}
                preserve={false}
                className={styles.form}
                layout="horizontal"
                labelCol={{ flex: "130px" }}
                wrapperCol={{ flex: "1 1 0", style: { minWidth: 0 } }}
                labelAlign="left"
                colon={false}
                initialValues={initialValues}
                onFinish={handleSubmit}
            >
                <div className={styles.activeRow}>
                    <span>{t("email_loop.form.section_status")}</span>
                    <Form.Item name="isActive" valuePropName="checked" noStyle>
                        <Switch />
                    </Form.Item>
                </div>

                <h4 className={styles.sectionTitle}>{t("email_loop.form.section_info")}</h4>
                <Form.Item
                    name="groupName"
                    label={t("email_loop.label.group_name")}
                    rules={[{ required: true, message: t("email_loop.form.group_name_required") }]}
                >
                    <Input placeholder={t("email_loop.form.group_name_placeholder")} />
                </Form.Item>
                <Form.Item name="description" label={t("email_loop.label.description")}>
                    <TextArea rows={2} maxLength={200} showCount placeholder={t("email_loop.form.description_placeholder")} />
                </Form.Item>

                <Divider style={{ margin: "24px 0 16px" }} />
                <h4 className={styles.sectionTitle}>{t("email_loop.form.section_condition")}</h4>
                <div className={styles.hint}>{t("email_loop.form.condition_hint")}</div>

                <Form.Item name="departmentIdList" label={t("email_loop.label.department")}>
                    <Select
                        mode="multiple"
                        allowClear
                        options={withAllOption(departmentOptions)}
                        placeholder={t("email_loop.form.select_department")}
                        onChange={handleSelectChange("departmentIdList")}
                    />
                </Form.Item>
                <Form.Item name="positionIdList" label={t("email_loop.label.position")}>
                    <Select
                        mode="multiple"
                        allowClear
                        options={withAllOption(positionOptions)}
                        placeholder={t("email_loop.form.select_position")}
                        onChange={handleSelectChange("positionIdList")}
                    />
                </Form.Item>

                <Divider style={dividerStyle} />
                <h4 className={styles.sectionTitle}>{t("email_loop.form.section_preview")}</h4>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "#F9FAFB",
                        borderRadius: 8,
                        padding: "10px 14px",
                        marginBottom: 16,
                    }}
                >
                    <Space>
                        <Avatar size={32} icon={<TeamOutlined />} style={{ backgroundColor: "#E4E7EC" }} />
                        <span>{t("email_loop.form.member_found").replace("{count}", memberCount)}</span>
                    </Space>
                    <a onClick={() => onOpenMemberPreview(previewMembers)}>{t("email_loop.form.view_list")}</a>
                </div>

                <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                    <Button onClick={onCancel}>{t("action.cancel")}</Button>
                    <Button type="primary" htmlType="submit">{t("action.save")}</Button>
                </Space>
            </Form>
        </Modal>
    );
};

export default EmailLoopModal;